import React, { useMemo } from 'react';
import { Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import { useWorkspace } from '~/contexts';
import MyReportsPage from './MyReportsPage';
import ReportExportsPage from './ReportExportsPage';
import ReportPage from './ReportPage';
import { useReports } from './ReportsContext';
import ReportsLibraryPage from './ReportsLibraryPage';
import ReportSchedulesPage from './schedules/ReportSchedulesPage';

function ReportsArea() {
  const { path, url } = useRouteMatch();
  const { search } = useLocation();

  const reportsContext = useReports();

  const legacyRoutesRedirect = useMemo(() => {
    if (!reportsContext) return [];

    const reports = reportsContext.reports.filter((report) => report.scopeKey === 'system');

    const sections = {
      time: '/time',
      expenses: '/expenses',
      utilization: '/utilization',
      plan: '/plan-and-forecast',
      forecast: '/plan-and-forecast',
      variance: '/plan-and-forecast',
      accounting: '/financial',
      performance: '/financial',
      pipeline: '/pipeline',
      workspace: '/workspace',
    };

    return [
      ['/forecast', '/library'],
      ['/forecast/hours-forecast-by-project-and-resource', '/library/hours-plan-by-project-and-resource'],
      ['/forecast/hours-forecast-by-resource-and-project', '/library/hours-plan-by-resource-and-project'],
      ['/forecast/performance-forecast-by-project', '/library/performance-plan-by-project'],
      [
        '/forecast/services-revenue-forecast-by-client-and-project',
        '/library/services-revenue-plan-by-client-and-project',
      ],
      ['/forecast/utilization-forecast-by-member', '/library/utilization-plan-by-member'],
      ['/forecast/utilization-forecast-by-member-practice', '/library/utilization-plan-by-member-practice'],
      ['/variance', '/library'],
      ['/variance/hours-variance-by-member-and-project', '/library/hours-variance-by-member-and-project'],
      ['/variance/hours-variance-by-project-and-resource', '/library/hours-variance-by-project-and-resource'],
      ['/variance/hours-variance-by-project-and-role', '/library/hours-variance-by-project-and-role'],
      ['/time/time-detail', '/library/time-entries'],
      ['/expenses/expense-detail', '/library/expense-items'],
      ['/workspace/members-list', '/library/members'],
      ['/workspace/projects-list', '/library/projects'],
      ['/workspace/clients-list', '/library/clients'],
      ['/workspace/project-tasks-list', '/library/project-tasks'],
      ['/opportunity-detail', '/library/opportunities'],
      ['/library/opportunity-detail', '/library/opportunities'],
      ['/my-reports/opportunity-detail', '/my-reports/opportunities'],

      ...reports.map((report) => [`/${report.slug}`, `/library/${report.slug}`]),

      ...reports
        .filter((report) => sections[report.section])
        .map((report) => [`${sections[report.section]}/${report.slug}`, `/library/${report.slug}`]),
    ];
  }, [reportsContext]);

  const { workspace } = useWorkspace();

  return (
    <Switch>
      <Route path={path.concat('/my-reports')} exact>
        <MyReportsPage />
      </Route>

      <Route path={path.concat('/library')} exact>
        <ReportsLibraryPage />
      </Route>

      {legacyRoutesRedirect.map(([from, to]) => (
        <Redirect key={from} from={path.concat(from)} to={url.concat(to).concat(search)} exact />
      ))}

      <Route
        path={`${path}/:area(my-reports|library)/:slug/:name?`}
        exact
        render={({
          match: {
            params: { slug },
          },
        }) => {
          return <ReportPage key={slug} />;
        }}
      />

      <Route path={path.concat('/schedules')} exact>
        <ReportSchedulesPage />
      </Route>

      <Route path={path.concat('/exports')} exact>
        <ReportExportsPage />
      </Route>

      <Redirect to={`/app/${workspace.key}/reports/my-reports`} />
    </Switch>
  );
}

export default ReportsArea;
